import { NumberUtils } from '@modeso/aumico-lib-core-fe';
import {
    IFinancialLineItemComponent,
    IFli,
    IManualAccount,
    IMovementScheduleAccount,
    IMovementScheduleComponent,
    IMovementScheduleFli,
    INotesComponent,
    INotesSection,
    ISuperTableCalculationFunction,
    ISuperTableComponent,
    ISuperTableRow,
    ItemType,
    SuperTableRowType,
} from '@modeso/types__aumico-lib-annualreport-be';
import { ComponentType, SuperTableColumnType } from '@modeso/types__aumico-lib-notes-be';
import { TemplateType } from '@modeso/types__aumico-lib-templates-be';
import { cloneDeep, isEqual } from 'lodash-es';
import { FX_RESERVE_NODE_NAME } from '../interfaces/IFxNotesContext.interface';
import { IFLIForNotes } from '../interfaces/IFLIForNotes.interface';
import { MappingItemsSelectionNode } from '../interfaces/mapping-items-selection-node.interface';
import { ISuperTableComponentFormValue } from '../interfaces/super-table-component-form-value.interface';
import { ISuperTableComponentRowFormValue } from '../interfaces/super-table-component-row-form-value.interface';
import { SuperTableMapper } from '../mappers/super-table.mapper';
import { FliHelper } from './fli.helper';
import { SuperTableHelper } from './super-table.helper';

/** The live data the notes components derive their numbers from (REV-22548). */
export interface INotesLiveData {
    /** G&M-transformed FLIs with reclasses applied — what the FLI table and the movement schedule read. */
    flis: IFLIForNotes[];
    /** Selection trees the super table matches its mapped rows against. */
    balanceMappingItemsSelectionTree: MappingItemsSelectionNode | undefined;
    incomeMappingItemsSelectionTree: MappingItemsSelectionNode | undefined;
}

export interface INotesLiveRefreshOptions {
    /** A sealed report is never refreshed, so leaving its notes can never trigger a save. */
    isSealed: boolean;
}

export interface INotesLiveRefreshResult {
    accordions: INotesSection[];
    /** True when at least one mapping-derived component differs from the saved notes. */
    changed: boolean;
}

type MovementScheduleColumn = 'column1' | 'column2' | 'column3' | 'column4';
const MOVEMENT_SCHEDULE_COLUMNS: MovementScheduleColumn[] = ['column1', 'column2', 'column3', 'column4'];

/** Rows whose value is typed or calculated, never read from the mapping. */
const NON_MAPPED_SUPER_TABLE_ROW_TYPES: SuperTableRowType[] = [
    SuperTableRowType.TITLE,
    SuperTableRowType.SPACE,
    SuperTableRowType.TEXT_ROW,
    SuperTableRowType.FREE_TEXT,
    SuperTableRowType.CALCULATION,
    // FX (REV-21512): a rate is re-resolved at render time, never stored.
    SuperTableRowType.RATE,
];

/**
 * REV-22548: one page-level refresh of every mapping-derived notes component (FLI, movement
 * schedule, super table) against live data, so the notes page saves what it shows and the print
 * shows what was saved. Pure and idempotent; the same functions back «Daten aktualisieren».
 */
export class NotesLiveRefreshHelper {
    private static readonly fliHelper = new FliHelper();
    private static readonly superTableMapper = new SuperTableMapper();
    private static readonly superTableHelper = new SuperTableHelper();

    /**
     * Saved FLI tables take names, groups, values and added or removed accounts from live data, in
     * live order (`FliHelper.getUpdatedFlis`); the total is re-summed so the saved total is the one
     * the editor shows. FLIs that left the mapping are dropped.
     */
    static refreshFliComponent(
        component: IFinancialLineItemComponent,
        liveFlis: IFLIForNotes[],
    ): IFinancialLineItemComponent {
        const refreshed = cloneDeep(component);
        if (
            !Array.isArray(refreshed?.flis) ||
            refreshed.flis.length === 0 ||
            !NotesLiveRefreshHelper.hasItems(liveFlis)
        ) {
            return refreshed;
        }

        refreshed.flis = NotesLiveRefreshHelper.fliHelper
            .getUpdatedFlis(refreshed.flis, liveFlis)
            .map((fli: IFli) => NotesLiveRefreshHelper.withRoundedAccountsAndTotal(fli));

        return refreshed;
    }

    /**
     * Saved movement schedules take their accounts and opening/closing values from live data and keep
     * everything the user typed: the movement columns per account, the column headers, numbering.
     * The total row is recomputed from the merged accounts.
     */
    static refreshMovementScheduleComponent(
        component: IMovementScheduleComponent,
        liveFlis: IFLIForNotes[],
    ): IMovementScheduleComponent {
        const refreshed = cloneDeep(component);
        if (
            !Array.isArray(refreshed?.flis) ||
            refreshed.flis.length === 0 ||
            !NotesLiveRefreshHelper.hasItems(liveFlis)
        ) {
            return refreshed;
        }

        // Footnotes are bound to their FLI by index (the component drops the footnote at the same
        // index when a FLI is removed), so a FLI that left the mapping takes its own footnote along.
        const savedFootnotes = Array.isArray(refreshed.footnotes) ? refreshed.footnotes : null;
        const savedFliCount = refreshed.flis.length;
        const updatedFlis: IMovementScheduleFli[] = [];
        const updatedFootnotes: string[] = [];
        refreshed.flis.forEach((savedFli, index) => {
            const liveFli = liveFlis.find((fli) => fli.id === savedFli.id);
            if (liveFli == null) {
                return;
            }
            updatedFlis.push(NotesLiveRefreshHelper.mergeMovementScheduleFli(savedFli, liveFli));
            if (savedFootnotes != null && index < savedFootnotes.length) {
                updatedFootnotes.push(savedFootnotes[index]);
            }
        });
        refreshed.flis = updatedFlis;

        if (savedFootnotes != null && (updatedFlis.length < savedFliCount || savedFootnotes.length > savedFliCount)) {
            refreshed.footnotes = updatedFootnotes;
        }

        return refreshed;
    }

    /**
     * Saved super-table rows linked to mapping items take the final (reclass-applied) value of
     * those items from the live trees; calculation rows are recomputed from the refreshed rows.
     * Typed rows, free columns and rows whose items left the mapping stay as saved.
     */
    static refreshSuperTableComponent(
        component: ISuperTableComponent,
        balanceMappingItemsSelectionTree: MappingItemsSelectionNode | undefined,
        incomeMappingItemsSelectionTree: MappingItemsSelectionNode | undefined,
    ): ISuperTableComponent {
        const refreshed = cloneDeep(component);
        if (
            !Array.isArray(refreshed?.rows) ||
            (balanceMappingItemsSelectionTree == null && incomeMappingItemsSelectionTree == null)
        ) {
            return refreshed;
        }

        for (const row of refreshed.rows) {
            NotesLiveRefreshHelper.refreshMappedSuperTableRow(
                row,
                balanceMappingItemsSelectionTree,
                incomeMappingItemsSelectionTree,
            );
        }
        NotesLiveRefreshHelper.recalculateSuperTableCalculationRows(refreshed);

        return refreshed;
    }

    /**
     * The page-level step: refresh every mapping-derived component in every section — expanded or
     * not — and report whether anything differs from the saved notes. Components that hold only user
     * input, and everything around the data (section and component wrappers), stay as saved.
     */
    static refreshAccordions(
        accordions: INotesSection[],
        live: INotesLiveData,
        options: INotesLiveRefreshOptions,
    ): INotesLiveRefreshResult {
        const refreshedAccordions = cloneDeep(accordions ?? []);
        if (options.isSealed) {
            return { accordions: refreshedAccordions, changed: false };
        }

        let changed = false;
        for (const section of refreshedAccordions) {
            if (!Array.isArray(section?.data)) {
                continue;
            }
            section.data = section.data.map((component: INotesComponent) => {
                const refreshedData = NotesLiveRefreshHelper.refreshComponentData(component, live);
                if (
                    refreshedData === undefined ||
                    isEqual(
                        NotesLiveRefreshHelper.shownValues(component.componentName, refreshedData),
                        NotesLiveRefreshHelper.shownValues(component.componentName, component.data),
                    )
                ) {
                    return component;
                }
                changed = true;
                return { ...component, data: refreshedData };
            });
        }

        return { accordions: refreshedAccordions, changed };
    }

    /**
     * True when refreshing the saved notes against live data would change what the editor and the
     * print show (REV-22548, decision P1 refined: Abschluss & Druck warns only then, never on a
     * version number alone).
     */
    static isOutOfDate(accordions: INotesSection[], live: INotesLiveData): boolean {
        return NotesLiveRefreshHelper.refreshAccordions(accordions, live, { isSealed: false }).changed;
    }

    /**
     * Only what the editor and the print show — names, ids, rounded amounts, the user's movement
     * columns, totals and row values. A saved document can differ from a refreshed one in harmless
     * shape (an extra key, `null` vs a missing value, float noise); that must never count as a change,
     * or the page would ask to save and the print would warn for nothing.
     */
    private static shownValues(componentName: ComponentType, data: unknown): unknown {
        // A text row in a custom table keeps its text in the value fields ("Textzeile CY"): compared
        // as the text it is, never rounded.
        const amount = (value: unknown) => {
            if (value == null || value === '') {
                return null;
            }
            const number = Number(value);
            return Number.isFinite(number) ? NotesLiveRefreshHelper.round(number) : String(value);
        };
        const text = (value: unknown) => (value == null ? null : value);
        switch (componentName) {
            case ComponentType.FINANCIAL_LINE_ITEM:
                // The caption is left out on purpose: the live caption can be a locale object where the
                // saved one is the same text as a string (REV-21347) — same words, different shape.
                return ((data as IFinancialLineItemComponent)?.flis ?? []).map((fli) => ({
                    id: fli.id,
                    accounts: (fli.accounts ?? []).map((account) => [
                        String(account.id),
                        text(account.accountName),
                        amount(account.currentValue),
                        amount(account.previousValue),
                    ]),
                    total: [amount(fli.totalRow?.currentValue), amount(fli.totalRow?.previousValue)],
                }));
            case ComponentType.MOVEMENT_SCHEDULE:
                return ((data as IMovementScheduleComponent)?.flis ?? []).map((fli) => ({
                    id: fli.id,
                    accounts: (fli.accounts ?? []).map((account) => [
                        String(account.id),
                        text(account.text),
                        amount(account.openValue),
                        amount(account.closeValue),
                        ...MOVEMENT_SCHEDULE_COLUMNS.map((column) => amount(account[column])),
                    ]),
                    total: fli.totalRow
                        ? [
                              amount(fli.totalRow.openValue),
                              amount(fli.totalRow.closeValue),
                              ...MOVEMENT_SCHEDULE_COLUMNS.map((column) => amount(fli.totalRow[column])),
                          ]
                        : null,
                }));
            case ComponentType.SUPER_TABLE: {
                const table = data as ISuperTableComponent;
                const valueColumnIds = (table?.options?.xAxisConfig ?? [])
                    .filter((col) => col.type === SuperTableColumnType.NUMBER || col.type === SuperTableColumnType.CALCULATION)
                    .map((col) => col.columnId);
                return (table?.rows ?? []).map((row) => [
                    row?.rowId,
                    amount(row?.currentValue),
                    amount(row?.previousValue),
                    ...valueColumnIds.map((columnId) => amount(row?.[columnId])),
                ]);
            }
            default:
                return data;
        }
    }

    private static refreshComponentData(
        component: INotesComponent,
        live: INotesLiveData,
    ): INotesComponent['data'] | undefined {
        switch (component?.componentName) {
            case ComponentType.FINANCIAL_LINE_ITEM:
                return NotesLiveRefreshHelper.refreshFliComponent(
                    component.data as IFinancialLineItemComponent,
                    live.flis,
                );
            case ComponentType.MOVEMENT_SCHEDULE:
                return NotesLiveRefreshHelper.refreshMovementScheduleComponent(
                    component.data as IMovementScheduleComponent,
                    live.flis,
                );
            case ComponentType.SUPER_TABLE:
                return NotesLiveRefreshHelper.refreshSuperTableComponent(
                    component.data as ISuperTableComponent,
                    live.balanceMappingItemsSelectionTree,
                    live.incomeMappingItemsSelectionTree,
                );
            default:
                return undefined;
        }
    }

    private static hasItems<T>(list: T[] | null | undefined): boolean {
        return Array.isArray(list) && list.length > 0;
    }

    private static round(value: number): number {
        const number = Number(value ?? 0);
        // roundNumber throws on a non-finite value; such a value is kept as it is.
        return Number.isFinite(number) ? NumberUtils.roundNumber(number) : number;
    }

    private static withRoundedAccountsAndTotal(fli: IFli): IFli {
        const accounts: IManualAccount[] = (fli.accounts ?? []).map((account) => ({
            ...account,
            currentValue: NotesLiveRefreshHelper.round(account.currentValue),
            previousValue: NotesLiveRefreshHelper.round(account.previousValue),
        }));
        const sum = (key: 'currentValue' | 'previousValue') =>
            NotesLiveRefreshHelper.round(accounts.reduce((total, account) => total + account[key], 0));

        return {
            ...fli,
            accounts,
            totalRow: { ...fli.totalRow, currentValue: sum('currentValue'), previousValue: sum('previousValue') },
        };
    }

    /** Same live shape `MovementScheduleHelper.prepareMovementScheduleFlis` builds for the component. */
    private static liveMovementScheduleAccounts(liveFli: IFLIForNotes): IMovementScheduleAccount[] {
        return (liveFli.accounts ?? []).map((account) => ({
            id: account.id.toString(),
            text: account.accountName,
            closeValue: account.currentValue,
            openValue: account.previousValue,
            column1: null,
            column2: null,
            column3: null,
            column4: null,
        }));
    }

    private static mergeMovementScheduleFli(
        savedFli: IMovementScheduleFli,
        liveFli: IFLIForNotes,
    ): IMovementScheduleFli {
        const savedAccountsById = new Map((savedFli.accounts ?? []).map((account) => [account.id, account]));
        const accounts = NotesLiveRefreshHelper.liveMovementScheduleAccounts(liveFli).map((liveAccount) => {
            const savedAccount = savedAccountsById.get(liveAccount.id);
            if (savedAccount == null) {
                return liveAccount;
            }
            const merged = { ...liveAccount };
            for (const column of MOVEMENT_SCHEDULE_COLUMNS) {
                merged[column] = savedAccount[column] ?? null;
            }
            return merged;
        });

        const merged: IMovementScheduleFli & { itemType?: ItemType } = {
            ...savedFli,
            name: liveFli.name,
            caption:
                liveFli.caption != null
                    ? { 'de-ch': liveFli.caption, 'en-us': liveFli.caption }
                    : { 'de-ch': liveFli.name },
            position: liveFli.position,
            templateType: liveFli.belongingTemplateType,
            itemType: liveFli.itemType,
            accounts,
        };
        if (merged.name === undefined) {
            delete merged.name;
        }

        if (savedFli.totalRow != null && accounts.length > 0) {
            const sum = (pick: (account: IMovementScheduleAccount) => number | null) =>
                accounts.reduce((total, account) => total + (pick(account) ?? 0), 0);
            merged.totalRow = {
                ...savedFli.totalRow,
                closeValue: sum((account) => account.closeValue),
                openValue: sum((account) => account.openValue),
                column1: sum((account) => account.column1),
                column2: sum((account) => account.column2),
                column3: sum((account) => account.column3),
                column4: sum((account) => account.column4),
            };
        }

        return merged;
    }

    /** The final value of a mapping node: imported balance plus its aggregated reclass (REV-20899). */
    private static finalValue(
        balance: number | null | undefined,
        reclass: number | null | undefined,
    ): number | undefined {
        if (balance == null && (reclass == null || reclass === 0)) {
            return balance ?? undefined;
        }
        return (balance ?? 0) + (reclass ?? 0);
    }

    private static refreshMappedSuperTableRow(
        row: ISuperTableRow,
        balanceTree: MappingItemsSelectionNode | undefined,
        incomeTree: MappingItemsSelectionNode | undefined,
    ): void {
        if (
            row == null ||
            !NotesLiveRefreshHelper.hasItems(row.itemsIds) ||
            NON_MAPPED_SUPER_TABLE_ROW_TYPES.includes(row.type)
        ) {
            return;
        }

        let mappingTree: MappingItemsSelectionNode | undefined;
        if (row.financialtype === TemplateType.BALANCE) {
            mappingTree = balanceTree;
        } else if (row.financialtype === TemplateType.INCOME) {
            mappingTree = incomeTree;
        } else if (balanceTree != null && incomeTree != null) {
            mappingTree = {
                data: null,
                children: balanceTree.children.concat(incomeTree),
                type: ItemType.GROUP,
                financialType: null,
                expanded: true,
            };
        }
        if (mappingTree == null) {
            return;
        }

        const matchingNodes = NotesLiveRefreshHelper.superTableMapper.findMatchingNodesForRowItems(
            row as unknown as ISuperTableComponentRowFormValue,
            mappingTree,
        );
        if (matchingNodes.length === 0) {
            return;
        }

        type NodeValues = {
            name?: string;
            calculatedClosingBalance?: number | null;
            closingReClass?: number | null;
            calculatedOpeningBalance?: number | null;
            openingReClass?: number | null;
        };
        const nodeData = (node: MappingItemsSelectionNode) => (node.data ?? {}) as NodeValues;

        // FX (REV-21512): the translation reserve exists only in display currency — its functional
        // cells stay EMPTY, never 0.
        if (matchingNodes.every((node) => nodeData(node).name === FX_RESERVE_NODE_NAME)) {
            row.currentValue = undefined;
            row.previousValue = undefined;
            return;
        }

        row.currentValue = NumberUtils.sumIgnoringInfiniteNumbers(
            matchingNodes.map((node) =>
                NotesLiveRefreshHelper.finalValue(
                    nodeData(node).calculatedClosingBalance,
                    nodeData(node).closingReClass,
                ),
            ),
            0,
        );
        row.previousValue = NumberUtils.sumIgnoringInfiniteNumbers(
            matchingNodes.map((node) =>
                NotesLiveRefreshHelper.finalValue(
                    nodeData(node).calculatedOpeningBalance,
                    nodeData(node).openingReClass,
                ),
            ),
            0,
        );
    }

    /**
     * Re-run every row calculation (sum, difference, ratio, product) on the refreshed rows. Only the
     * computed values are written back, so the stored row keeps its title, format and shape.
     */
    private static recalculateSuperTableCalculationRows(table: ISuperTableComponent): void {
        const xAxisConfig = table.options?.xAxisConfig ?? [];
        const compareYears = table.options?.compareYears ?? true;
        const valueColumnIds = xAxisConfig
            .filter((col) => col.type === SuperTableColumnType.NUMBER || col.type === SuperTableColumnType.CALCULATION)
            .map((col) => col.columnId);

        for (const row of table.rows) {
            if (row?.type !== SuperTableRowType.CALCULATION) {
                continue;
            }
            const calculation = (table.calculations ?? []).find((calc) => calc.calcId === row.rowId);
            if (calculation == null) {
                continue;
            }

            const formValue = { rows: table.rows } as unknown as ISuperTableComponentFormValue;
            const helper = NotesLiveRefreshHelper.superTableHelper;
            let computed: ISuperTableComponentRowFormValue;
            switch (calculation.function) {
                case ISuperTableCalculationFunction.SUMMATION:
                    computed = helper.getSummationRow(calculation, formValue, xAxisConfig, compareYears);
                    break;
                case ISuperTableCalculationFunction.SUBTRACTION:
                    computed = helper.getSubtractionRow(calculation, formValue, xAxisConfig, compareYears);
                    break;
                case ISuperTableCalculationFunction.RATIO:
                    computed = helper.getRatioRow(calculation, formValue, xAxisConfig, compareYears);
                    break;
                case ISuperTableCalculationFunction.MULTIPLICATION:
                    computed = helper.getMultiplicationRow(calculation, formValue, xAxisConfig, compareYears);
                    break;
                default:
                    continue;
            }

            row.currentValue = computed.currentValue;
            if (compareYears) {
                row.previousValue = computed.previousValue;
            }
            for (const columnId of valueColumnIds) {
                if (computed[columnId] !== undefined) {
                    row[columnId] = computed[columnId] as number;
                }
            }
        }
    }
}
